<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { svgIcon, baseInput } from "@/common/components";

import { equipmentList } from "@/common/api/rest/equipment";
import { evaluationEquipmentList } from "@/common/api/rest/evaluation";

import type { EquipmentType } from "@/common/types/equipment";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = defineProps<{
  title?: string;
  accountId?: string;
  evaluationId?: string;
  unic?: boolean;
  ids?: string[];
}>();

const selecteds = ref<any[]>([]);
const listData = ref<EquipmentType[]>([]);
const inputSearch = ref<string | null>("");

function searchEvaluation() {
  if (!props.evaluationId) return;

  evaluationEquipmentList(props.evaluationId, {
    search: inputSearch.value as string,
    limit: 500,
    page: 0,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        listData.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function searchEquipments() {
  if (!props.accountId) return;

  equipmentList(props.accountId, {
    search: inputSearch.value as string,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        listData.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function search() {
  if (props.evaluationId) {
    searchEvaluation();
    return;
  }

  searchEquipments();
}

function isDisabled(data: EquipmentType) {
  if (props.ids?.includes(data.equipment_id)) {
    return true;
  }

  return false;
}

function select(data: EquipmentType) {
  if (isDisabled(data)) {
    return;
  }

  if (props.unic) {
    selecteds.value = [data];
  } else {
    if (selecteds.value.find((sl) => sl.equipment_id === data.equipment_id)) {
      selecteds.value = selecteds.value.filter(
        (sl) => sl.equipment_id !== data.equipment_id,
      );
    } else {
      selecteds.value.push({
        equipment_id: data.equipment_id,
        name: data.name,
        code: data.code,
      });
    }
  }

  emit("update-data", selecteds.value);
}

onMounted(() => {
  search();
});
</script>

<template>
  <section class="technical-form">
    <div class="title">
      {{ title }}
    </div>
    <div class="search">
      <base-input
        v-model="inputSearch"
        background="#fff"
        color="#000"
        :placeholder="t('common.labels.search')"
        @key:enter="search()"
      >
        <template #sufix>
          <div class="icon">
            <svg-icon icon="search" size="1.75rem" @click="search()" />
          </div>
        </template>
      </base-input>
    </div>

    <div class="list">
      <template v-for="item in listData" :key="item.equipment_id">
        <div
          class="item"
          @click="select(item)"
          :class="{
            active: selecteds.find(
              (sl) => sl.equipment_id === item.equipment_id,
            ),
            disabled: isDisabled(item),
          }"
        >
          <div class="avatar">
            <template v-if="item.avatar">
              <img :src="item.avatar.small" />
            </template>
            <template v-else>
              <svg-icon icon="no-image" size="2rem" />
            </template>
          </div>
          <div class="infos">
            <div class="name">
              {{ item.code }}
            </div>
            <div class="type">
              {{ item.name }}
            </div>
          </div>
        </div>
      </template>
      <template v-if="listData.length === 0">
        <div class="empty">
          {{ t("common.texts.no_results") }}
        </div>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.technical-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    width: calc(100% - 0.5rem);
    height: 50dvh;
    overflow-y: scroll;
    padding-inline-end: 0.5rem;
    gap: 0.5rem;

    .item {
      cursor: pointer;
      display: flex;
      flex-direction: row;
      gap: 1rem;
      border: 1px solid var(--border);
      padding-block: 0.5rem;
      padding-inline: 0.5rem;
      border-radius: 0.5rem;

      &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      &.active {
        border-color: var(--primary);
        background: var(--primary);
        color: var(--primary-color);
      }

      .avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3.75rem;
        height: 3.75rem;
        border-radius: 5px;
        overflow: hidden;
        background: var(--white-off);
        color: var(--text-off);

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .name {
        font-weight: bold;
      }
    }
  }
}
</style>
